import { useState } from "react";
import { useAuth, Show } from "@clerk/react";
import { Navigate } from "react-router-dom";
import { makeApi } from "@/api";
import { Sparkle, Microphone, Megaphone, Rocket, ArrowRight } from "@phosphor-icons/react";

const MODES = [
  { k: "bio", name: "Artist Bio", icon: <Microphone size={22} />,
    hint: "e.g. 'I'm a London-based synthwave producer blending analog warmth with brutalist drums. Debut EP out this fall.'",
    placeholder: "Describe yourself, your sound, your story..."
  },
  { k: "release", name: "Release Strategy", icon: <Rocket size={22} />,
    hint: "e.g. 'Dropping a 4-track EP in 6 weeks. Small audience (~800 followers). Genre: dark electronic.'",
    placeholder: "Describe your release — timeline, genre, size of audience..."
  },
  { k: "marketing", name: "Marketing Tactics", icon: <Megaphone size={22} />,
    hint: "e.g. 'Lo-fi hip-hop, 10K monthly on Spotify, want to grow TikTok and get on editorial playlists.'",
    placeholder: "What are you promoting? Who's your audience?"
  },
];

function Coach() {
  const { getToken } = useAuth();
  const api = makeApi(getToken);
  const [mode, setMode] = useState("bio");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState("");

  const active = MODES.find(m => m.k === mode);

  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setOut("");
    try {
      const r = await api.post("/ai/coach", { mode, prompt });
      setOut(r.data.response);
    } catch (e) {
      setOut("The coach is thinking too hard. Try again — " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="player-space px-4 md:px-10 py-12">
      <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-red-500 mb-3">AI / Career Development</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter uppercase leading-[0.9]">The Coach</h1>
      <p className="font-editorial text-2xl text-stone-300 mt-6 max-w-3xl">
        "Every indie artist deserves a label-grade team. We handed ours over to Claude."
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-12">
        {MODES.map((m) => (
          <button
            key={m.k}
            onClick={() => { setMode(m.k); setOut(""); }}
            data-testid={`coach-mode-${m.k}`}
            className={`text-left p-6 border transition-colors ${mode === m.k ? "bg-neutral-950 border-red-600" : "hard-card hover:border-neutral-600"}`}
          >
            <div className={`${mode === m.k ? "text-red-500" : "text-neutral-400"}`}>{m.icon}</div>
            <div className="font-display text-2xl font-bold mt-4">{m.name}</div>
            <div className="font-editorial text-base text-neutral-400 mt-2">{m.hint}</div>
          </button>
        ))}
      </div>

      <div className="hard-card mt-10 p-8">
        <label className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-neutral-400">Your Brief</label>
        <textarea
          data-testid="coach-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={active.placeholder}
          rows={5}
          className="w-full bg-transparent border-b border-neutral-700 focus:border-red-600 outline-none py-3 font-mono-ui text-sm mt-2 resize-none transition-colors"
        />
        <div className="flex items-center gap-4 mt-6">
          <button
            onClick={run}
            disabled={loading || !prompt.trim()}
            data-testid="coach-run-btn"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.3em] transition-colors"
          >
            <Sparkle size={14} weight="fill" />
            {loading ? "Composing..." : "Generate"}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {out && (
        <div className="hard-card-elev mt-6 p-8 whitespace-pre-wrap font-editorial text-xl leading-relaxed text-stone-200" data-testid="coach-output">
          {out}
        </div>
      )}
    </div>
  );
}

export default function CoachPage() {
  return (
    <>
      <Show when="signed-in"><Coach /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
